<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import AppLayout from '../layouts/AppLayout.vue'
import { fetchAdvisorSuppliers, type AdvisorSupplierCard, type PeopleSearchResult } from '../services/supplierApi'
import { useAuth } from '../composables/useAuth'
import { useRouter } from 'vue-router'
import SupplierLogo from '../components/SupplierLogo.vue'
import SupplierPreferenceActions from '../components/SupplierPreferenceActions.vue'
import { useSupplierPreferencesStore } from '../stores/supplierPreferences'
import { useUserStore } from '../stores/user'
import { trackEvent } from '../services/analytics'
import CustomSelect from '../components/CustomSelect.vue'
import { useFuzzySupplierSearch } from '../composables/useFuzzySupplierSearch'

const { getAccessTokenSilently } = useAuth()
const router = useRouter()
const prefsStore = useSupplierPreferencesStore()
const userStore = useUserStore()
const { loadSupplierNames, fuzzySearchSuppliers } = useFuzzySupplierSearch()

const suppliers = ref<AdvisorSupplierCard[]>([])
const people = ref<PeopleSearchResult[]>([])
const loading = ref(true)
const error = ref<string | null>(null)
const searchQuery = ref('')
const fuzzySuggestions = ref<string[]>([])
const usingFuzzyResults = ref(false)
let searchDebounce: ReturnType<typeof setTimeout> | null = null

// Filters & Pagination
// Default to 'My TSDs' if the advisor has TSD affiliations, otherwise 'All'
const userTsdNames = [...userStore.advisorTsdNames]
const tsdFilter = ref(userTsdNames.length > 0 ? 'My TSDs' : 'All')
const tsdOptions = userTsdNames.length > 0
    ? ['My TSDs', 'All', ...userTsdNames]
    : ['All']

const page = ref(1)
const limit = ref<number | 'all'>(50)
const totalSuppliers = ref(0)

// Compare mode state
const compareMode = ref(false)
const selectedSupplierIds = ref<string[]>(['', '', ''])
const allSuppliersForCompare = ref<AdvisorSupplierCard[]>([])

const canCompare = computed(() => {
    const filled = selectedSupplierIds.value.filter(id => id !== '')
    const unique = new Set(filled)
    return filled.length >= 2 && unique.size === filled.length
})

async function toggleCompareMode() {
    compareMode.value = !compareMode.value
    compareSearches.value = ['', '', '']
    if (!compareMode.value) {
        selectedSupplierIds.value = ['', '', '']
    } else if (allSuppliersForCompare.value.length === 0) {
        // Load all supplier names for the compare dropdowns
        try {
            const token = await getAccessTokenSilently()
            const params: Record<string, string> = { limit: 'all' }
            if (tsdFilter.value && tsdFilter.value !== 'All') params.tsd = tsdFilter.value
            if (searchQuery.value) params.search = searchQuery.value
            const { suppliers: all } = await fetchAdvisorSuppliers(params, token)
            allSuppliersForCompare.value = all
        } catch (e) {
            console.error('Failed to load suppliers for compare:', e)
            allSuppliersForCompare.value = suppliers.value // fallback to current page
        }
    }
}

// Compare search/filter
const compareSearches = ref<string[]>(['', '', ''])
function filteredCompareSuppliers(idx: number) {
    const q = compareSearches.value[idx]?.toLowerCase().trim()
    const list = allSuppliersForCompare.value
    if (!q) return list.slice(0, 30)
    return list.filter(s => s.name.toLowerCase().includes(q)).slice(0, 30)
}
function selectCompareSupplier(idx: number, supplier: AdvisorSupplierCard) {
    selectedSupplierIds.value[idx] = supplier.id
    compareSearches.value[idx] = supplier.name
}
function clearCompareSupplier(idx: number) {
    selectedSupplierIds.value[idx] = ''
    compareSearches.value[idx] = ''
}

function goToComparison() {
    const ids = selectedSupplierIds.value.filter(id => id !== '')
    if (ids.length >= 2) {
        trackEvent('supplier_compare_initiated', {
            supplier_count: ids.length,
        })
        router.push({ path: '/advisor/suppliers/compare', query: { ids: ids.join(',') } })
    }
}

async function loadSuppliers() {
    loading.value = true
    error.value = null
    fuzzySuggestions.value = []
    usingFuzzyResults.value = false
    try {
        const token = await getAccessTokenSilently()
        // Resolve 'My TSDs' to comma-separated list of user's TSD names
        const tsdParam = tsdFilter.value === 'My TSDs'
            ? userTsdNames.join(',')
            : tsdFilter.value
        const result = await fetchAdvisorSuppliers({
            search: searchQuery.value,
            tsd: tsdParam,
            page: page.value,
            limit: limit.value
        }, token)

        suppliers.value = result.suppliers
        totalSuppliers.value = result.total
        people.value = result.people || []

        // If exact search returned no results, try fuzzy matching
        if (searchQuery.value && result.total === 0) {
            const fuzzy = fuzzySearchSuppliers(searchQuery.value)
            if (fuzzy.ids.length > 0) {
                fuzzySuggestions.value = fuzzy.suggestions
                // Load fuzzy-matched suppliers from the server (paginated)
                const fuzzyResult = await fetchAdvisorSuppliers({
                    ids: fuzzy.ids,
                    tsd: tsdParam,
                    page: page.value,
                    limit: limit.value
                }, token)
                suppliers.value = fuzzyResult.suppliers
                totalSuppliers.value = fuzzyResult.total
                usingFuzzyResults.value = true
            }
        }

        if (searchQuery.value) {
            trackEvent('supplier_directory_searched', {
                query: searchQuery.value,
                result_count: result.suppliers.length,
                tsd_filter: tsdFilter.value,
                fuzzy_fallback: usingFuzzyResults.value,
            })
        }
    } catch (e) {
        console.error(e)
        error.value = "Unable to load supplier directory."
    } finally {
        loading.value = false
    }
}

// Debounced search
watch(searchQuery, () => {
    page.value = 1 // Reset to first page on search
    if (searchDebounce) clearTimeout(searchDebounce)
    searchDebounce = setTimeout(() => {
        loadSuppliers()
    }, 400)
})

// Watch filters
watch([tsdFilter, limit], () => {
    page.value = 1
    loadSuppliers()
})

// Watch page
watch(page, () => {
    loadSuppliers()
})

// Filter out blocked suppliers
const visibleSuppliers = computed(() =>
    suppliers.value.filter(s => !prefsStore.isBlocked(s.id))
)

onMounted(async () => {
    loadSuppliers()
    if (!prefsStore.loaded) prefsStore.load()
    // Load fuzzy search index in background
    try {
        const token = await getAccessTokenSilently()
        await loadSupplierNames(token)
    } catch { /* non-critical */ }
})

const navigateToProfile = (id: string) => {
    router.push(`/advisor/suppliers/${id}`)
}
</script>

<template>
    <AppLayout>
        <div class="space-y-6">
            <!-- Header & Controls -->
            <div class="flex flex-col gap-4">
                <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h1 class="text-3xl font-bold text-cc-text tracking-tight">Supplier Directory</h1>
                        <p class="text-cc-text-secondary mt-1">Discover partners, training, and exclusive promotions.</p>
                    </div>
                </div>

                <!-- Filters Toolbar -->
                <div class="flex flex-col sm:flex-row gap-3 p-4 bg-cc-elevated rounded-cc shadow-cc-card">
                    <!-- Search -->
                    <div class="relative flex-1">
                        <span class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400"><svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg></span>
                        <input
                            v-model="searchQuery"
                            type="text"
                            placeholder="Search suppliers, products, people..."
                            class="block w-full pl-10 pr-10 py-2.5 border border-cc-border rounded-cc bg-cc-elevated text-cc-text placeholder-gray-400 focus:ring-2 focus:ring-cc-primary focus:border-transparent outline-none text-sm"
                        />
                         <button 
                            v-if="searchQuery"
                            @click="searchQuery = ''"
                            class="absolute inset-y-0 right-0 pr-3 flex items-center text-cc-text-secondary hover:text-cc-text"
                            title="Clear search"
                        >
                            ✕
                        </button>
                    </div>

                    <!-- TSD Filter -->
                    <CustomSelect
                        v-model="tsdFilter"
                        :options="tsdOptions.map(o => ({ value: o, label: o === 'All' ? 'All TSDs' : o }))"
                        class="w-full sm:w-48"
                    />

                    <!-- Page Size -->
                    <CustomSelect
                        :modelValue="String(limit)"
                        @update:modelValue="limit = $event === 'all' ? 'all' : Number($event)"
                        :options="[
                            { value: '50', label: '50 per page' },
                            { value: '100', label: '100 per page' },
                            { value: 'all', label: 'Show All' }
                        ]"
                        class="w-full sm:w-36"
                    />

                    <!-- Compare Button -->
                    <button
                        @click="toggleCompareMode"
                        class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-cc border transition-colors"
                        :class="compareMode
                            ? 'bg-cc-primary text-white border-cc-primary hover:opacity-90'
                            : 'bg-cc-elevated text-cc-text-secondary border-cc-border-med hover:bg-cc-hover'"
                    >
                        {{ compareMode ? 'Cancel Compare' : 'Compare Suppliers' }}
                    </button>
                </div>
            </div>

            <!-- Compare Mode Panel -->
            <div v-if="compareMode" class="bg-cc-primary-subtle border border-cc-border rounded-cc p-5">
                <div class="flex flex-col lg:flex-row lg:items-end gap-4">
                    <div class="flex-1">
                        <h3 class="text-sm font-semibold text-cc-text mb-3">Select up to 3 suppliers to compare</h3>
                        <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div v-for="(_, idx) in selectedSupplierIds" :key="idx" class="relative">
                                <label class="block text-xs font-medium text-cc-primary mb-1">
                                    Supplier {{ idx + 1 }}{{ idx < 2 ? ' *' : ' (optional)' }}
                                </label>
                                <div class="relative">
                                    <input
                                        v-model="compareSearches[idx]"
                                        type="text"
                                        :placeholder="selectedSupplierIds[idx] ? '' : 'Search suppliers...'"
                                        autocomplete="off"
                                        class="block w-full px-3 pr-8 py-2 border border-cc-border rounded-cc bg-cc-elevated text-cc-text text-sm focus:ring-2 focus:ring-cc-primary focus:border-transparent outline-none"
                                        @focus="compareSearches[idx] = compareSearches[idx] || ''"
                                    />
                                    <button v-if="selectedSupplierIds[idx]" @click="clearCompareSupplier(idx)" type="button" class="absolute inset-y-0 right-0 pr-2 flex items-center text-gray-400 hover:text-red-500">
                                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
                                    </button>
                                </div>
                                <div v-if="!selectedSupplierIds[idx] && compareSearches[idx] !== undefined" class="absolute z-50 mt-1 w-full max-h-40 overflow-y-auto bg-cc-elevated border border-cc-border rounded-cc shadow-cc-dropdown">
                                    <button
                                        v-for="s in filteredCompareSuppliers(idx)"
                                        :key="s.id"
                                        type="button"
                                        :disabled="selectedSupplierIds.includes(s.id)"
                                        @mousedown.prevent="selectCompareSupplier(idx, s)"
                                        class="w-full text-left px-3 py-1.5 text-sm transition-colors"
                                        :class="selectedSupplierIds.includes(s.id) ? 'text-cc-text-secondary opacity-50 cursor-not-allowed' : 'text-cc-text hover:bg-cc-hover'"
                                    >
                                        {{ s.name }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <button
                        @click="goToComparison"
                        :disabled="!canCompare"
                        class="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium rounded-cc transition-colors whitespace-nowrap"
                        :class="canCompare
                            ? 'bg-cc-primary text-white hover:opacity-90'
                            : 'bg-cc-hover text-cc-text-secondary cursor-not-allowed'"
                    >
                        Compare Selected
                    </button>
                </div>
            </div>

            <!-- People Results -->
            <div v-if="people.length > 0 && searchQuery" class="bg-cc-elevated rounded-cc shadow-cc-card p-4">
                <h3 class="text-sm font-semibold text-cc-text-secondary mb-3 flex items-center gap-2">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                    People ({{ people.length }})
                </h3>
                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                    <div
                        v-for="person in people"
                        :key="person.id"
                        @click="navigateToProfile(person.supplier.id)"
                        class="flex items-center gap-3 p-3 rounded-cc border border-cc-border hover:bg-cc-hover cursor-pointer transition-colors"
                    >
                        <SupplierLogo :src="person.supplier.logoUrl" class="w-8 h-8 rounded-full bg-cc-hover flex-shrink-0" />
                        <div class="min-w-0">
                            <p class="text-sm font-medium text-cc-text truncate">{{ person.name }}</p>
                            <p v-if="person.jobTitle" class="text-xs text-cc-text-secondary truncate">{{ person.jobTitle }}</p>
                            <p class="text-xs text-cc-text-secondary truncate">{{ person.supplier.name }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Content -->
            <div v-if="loading && suppliers.length === 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                <!-- Skeletons -->
                 <div v-for="i in 8" :key="i" class="bg-cc-elevated h-64 rounded-cc animate-pulse"></div>
            </div>

            <div v-else-if="error" class="bg-red-50 border border-red-200 text-red-700 p-4 rounded-cc text-center">
                {{ error }}
            </div>

            <div v-else-if="visibleSuppliers.length === 0 && !usingFuzzyResults" class="text-center py-12 text-cc-text-secondary">
                <p class="text-lg">No suppliers found matching current filters.</p>
                <button @click="{ searchQuery = ''; tsdFilter = 'All'; }" class="mt-2 text-cc-primary hover:underline">Clear filters</button>
            </div>

            <!-- Fuzzy match banner -->
            <div v-if="usingFuzzyResults && fuzzySuggestions.length > 0" class="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-cc p-4 flex items-start gap-3">
                <svg class="w-5 h-5 text-amber-500 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
                <div>
                    <p class="text-sm text-amber-800 dark:text-amber-200">
                        No exact match for "<span class="font-semibold">{{ searchQuery }}</span>". Showing similar results:
                    </p>
                    <div class="flex flex-wrap gap-2 mt-2">
                        <button
                            v-for="suggestion in fuzzySuggestions"
                            :key="suggestion"
                            @click="searchQuery = suggestion"
                            class="text-sm font-medium text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/40 hover:bg-amber-200 dark:hover:bg-amber-900/60 px-2.5 py-1 rounded-md transition-colors"
                        >
                            {{ suggestion }}
                        </button>
                    </div>
                </div>
            </div>

            <div v-if="(visibleSuppliers.length > 0 || usingFuzzyResults) && !loading && !error" class="space-y-6">
                <!-- Grid -->
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    <div
                        v-for="supplier in visibleSuppliers"
                        :key="supplier.id"
                        @click="navigateToProfile(supplier.id)"
                        class="bg-cc-elevated rounded-cc shadow-cc-card p-5 cursor-pointer hover:shadow-lg hover:-translate-y-1 transition-all duration-200 group relative flex flex-col h-full"
                    >
                        <!-- Promo Badge -->
                        <div v-if="supplier.hasActivePromo" class="absolute top-0 right-0 bg-green-500 text-white text-[10px] font-bold px-2 py-1 rounded-bl-lg uppercase shadow-sm">
                            Promo Active
                        </div>

                        <!-- Header -->
                        <div class="flex items-start justify-between mb-4">
                            <div class="flex items-center gap-2">
                                <div class="h-12 w-12 bg-cc-elevated p-1 rounded-cc border border-cc-border flex items-center justify-center overflow-hidden">
                                    <SupplierLogo
                                        :src="supplier?.logoUrl"
                                        :alt="supplier.name"
                                        class="object-contain max-h-full max-w-full"
                                    />
                                </div>
                                <!-- Preference indicators -->
                                <span v-if="prefsStore.isFavorite(supplier.id)" class="text-yellow-500" title="Favorite">
                                    <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
                                </span>
                                <span v-if="prefsStore.isDirectContract(supplier.id)" class="text-xs px-1.5 py-0.5 rounded bg-cc-primary-subtle text-cc-primary font-medium">Direct</span>
                            </div>
                            <SupplierPreferenceActions :supplier-id="supplier.id" :compact="true" />
                        </div>

                        <!-- Body -->
                        <div class="flex-1">
                            <h3 class="font-bold text-lg text-cc-text group-hover:text-cc-primary transition truncate">{{ supplier.name }}</h3>
                            <p class="text-sm text-cc-text-secondary mt-2 line-clamp-2 mb-3 h-10">
                                {{ supplier.shortDescription }}
                            </p>
                            
                            <div class="flex flex-wrap gap-1.5 mb-4">
                                <span v-for="tag in (supplier.tags || []).slice(0,3)" :key="tag" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-cc-hover text-cc-text-secondary">
                                    {{ tag }}
                                </span>
                            </div>

                            <!-- Intelligence row -->
                            <div v-if="supplier.channelInvestmentScore || supplier.employeeCount || supplier.foundedYear" class="flex flex-wrap items-center gap-x-3 gap-y-1 mb-3 text-[10px] text-cc-text-tertiary">
                                <span v-if="supplier.employeeCount" class="font-medium">{{ supplier.employeeCount.toLocaleString() }} emp</span>
                                <span v-if="supplier.foundedYear">Est. {{ supplier.foundedYear }}</span>
                                <span v-if="supplier.headquarters">{{ supplier.headquarters }}</span>
                                <span v-if="supplier.ownershipType" class="font-semibold text-cc-text-secondary">{{ supplier.ownershipType }}</span>
                                <span v-if="supplier.employeeGrowthYoyPct != null"
                                  :class="supplier.employeeGrowthYoyPct >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500'">
                                  {{ supplier.employeeGrowthYoyPct >= 0 ? '+' : '' }}{{ supplier.employeeGrowthYoyPct }}% YoY
                                </span>
                            </div>
                            <!-- Scores -->
                            <div v-if="supplier.channelInvestmentScore || supplier.productDepthScore || supplier.financialStabilityScore" class="flex gap-2 mb-3">
                                <span v-if="supplier.channelInvestmentScore" class="text-[9px] font-bold px-1.5 py-0.5 rounded"
                                  :class="supplier.channelInvestmentScore >= 7 ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : supplier.channelInvestmentScore >= 4 ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'">
                                  CH {{ supplier.channelInvestmentScore }}/10
                                </span>
                                <span v-if="supplier.productDepthScore" class="text-[9px] font-bold px-1.5 py-0.5 rounded"
                                  :class="supplier.productDepthScore >= 7 ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : supplier.productDepthScore >= 4 ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'">
                                  PD {{ supplier.productDepthScore }}/10
                                </span>
                                <span v-if="supplier.financialStabilityScore" class="text-[9px] font-bold px-1.5 py-0.5 rounded"
                                  :class="supplier.financialStabilityScore >= 7 ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : supplier.financialStabilityScore >= 4 ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'">
                                  FS {{ supplier.financialStabilityScore }}/10
                                </span>
                            </div>

                            <!-- TSD Affiliations -->
                            <div v-if="supplier.tsdAffiliations && supplier.tsdAffiliations.length > 0" class="flex flex-wrap gap-1.5 mb-2">
                                 <span v-for="tsd in supplier.tsdAffiliations" :key="tsd.id" class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-cc-primary-subtle text-cc-primary border border-cc-border">
                                    {{ tsd.name }}
                                </span>
                            </div>
                        </div>

                        <!-- Footer -->
                        <div class="mt-4 pt-4 border-t border-cc-border flex items-center justify-between text-xs text-cc-text-secondary">
                            <div class="flex gap-3">
                                <span>{{ supplier.productCount }} Products</span>
                                <span>{{ supplier.resourceCount }} Resources</span>
                            </div>
                            <button
                                @click.stop="router.push({ path: '/advisor/quotes/new', query: { supplierId: supplier.id, supplierName: supplier.name } })"
                                class="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-cc-primary bg-cc-primary-subtle hover:opacity-90 rounded-md transition"
                                title="Quick Quote"
                            >
                                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                Quote
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Pagination Footer -->
                <div v-if="limit !== 'all' && totalSuppliers > 0" class="flex items-center justify-between pt-4 border-t border-cc-border">
                    <div class="text-sm text-cc-text-secondary">
                        Showing <span class="font-medium">{{ ((page - 1) * (limit as number)) + 1 }}</span> to <span class="font-medium">{{ Math.min(page * (limit as number), totalSuppliers) }}</span> of <span class="font-medium">{{ totalSuppliers }}</span> results
                    </div>
                    <div class="flex gap-2">
                        <button 
                            @click="page > 1 && page--" 
                            :disabled="page === 1"
                            class="px-3 py-1 border border-cc-border-med rounded-md text-sm text-cc-text disabled:opacity-50 disabled:cursor-not-allowed hover:bg-cc-hover"
                        >
                            Previous
                        </button>
                        <button
                            @click="page * (limit as number) < totalSuppliers && page++"
                            :disabled="page * (limit as number) >= totalSuppliers"
                            class="px-3 py-1 border border-cc-border-med rounded-md text-sm text-cc-text disabled:opacity-50 disabled:cursor-not-allowed hover:bg-cc-hover"
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </AppLayout>
</template>
